import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowRight, ChevronRight, HeartHandshake, MapPin, Ticket, X } from "lucide-react";
import { SPECTACLES } from "../pages/Spectacles";
import { Lien, versPageDuSite } from "./Lien";

/* Nos spectacles (Jen, 31.10.2026 : « tous nos spectacles mis en avant de la même façon » ;
   03.11.2026 : « pas aligné entre Noël et juin, très grand ») — une liste groupée façon
   iPhone (Cartes Wallet, Événements) : chaque spectacle sur UNE ligne de même hauteur, la
   vignette (affiche ou photo, même taille pour tous), la date, le titre, le lieu en un mot
   et, à droite, l'action en pastille (« Réserver », « Billets », « Ma réservation ») ou « Bientôt ».
   Toucher la ligne ouvre la fiche du spectacle (affiche entière, résumé, représentations,
   itinéraire, le bouton). La page Billets reçoit le nom, l'e-mail et le téléphone du compte
   (même site, sessionStorage : rien dans l'adresse). */
const CLE_CONTACT = "billets_contact";
const MOIS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const JOURS = ["Dim.", "Lun.", "Mar.", "Mer.", "Jeu.", "Ven.", "Sam."];

const morceaux = (iso) => String(iso || "").split("-").map(Number);
// « 7 déc. 2026 », « 12–13 juin 2027 ».
export const dateCourte = (s) => {
  if (!s.date_iso) return s.date_label || "";
  const [a, m, j] = morceaux(s.date_iso);
  if (s.date_fin_iso && s.date_fin_iso !== s.date_iso) {
    const [a2, m2, j2] = morceaux(s.date_fin_iso);
    if (a2 === a && m2 === m) return `${j}–${j2} ${MOIS[m - 1]} ${a}`;
    return `${j} ${MOIS[m - 1]} – ${j2} ${MOIS[m2 - 1]} ${a2}`;
  }
  return `${j} ${MOIS[m - 1]} ${a}`;
};
// Le lieu en un mot : la ville (« 1410 Thierrens » → « Thierrens », « … Cugy VD » → « Cugy VD »).
export const villeDe = (lieu) => {
  const parts = String(lieu || "").split(",").map((x) => x.trim()).filter(Boolean);
  return (parts[parts.length - 1] || "").replace(/^\d{4}\s+/, "");
};
// « Sam. 12 juin · 18h »
const representation = (r) => {
  const [a, m, j] = morceaux(r.date);
  const jour = JOURS[new Date(a, m - 1, j).getDay()];
  return `${jour} ${j} ${MOIS[m - 1]} · ${String(r.heure || "").replace(":00", "h").replace(":", "h")}`;
};

const action = (s, moi, billetsOuverts) => {
  // Noël déjà réservé : l'action mène à SA réservation (changer le nombre ou annuler).
  const sienne = !s.billets && (moi.reservations || []).find((r) => r.type === "spectacle" && r.lien
    && String(r.titre || "").trim().toLowerCase() === String(s.title || "").trim().toLowerCase());
  if (s.billets) {
    return billetsOuverts
      ? { lien: `/spectacles/billets?spectacle=${s.billets}`, court: "Billets", long: "Prendre mes billets", billets: true }
      : { lien: "", court: "Bientôt", long: "Billets en vente bientôt" };
  }
  if (sienne) return { lien: versPageDuSite(sienne.lien), court: "Ma réservation", long: "Modifier ma réservation", reserve: true };
  return s.reservation_url ? { lien: s.reservation_url, court: "Réserver", long: "Réserver ma place" } : { lien: "", court: "", long: "" };
};

const Vignette = ({ s, taille = "w-[52px] h-[70px]" }) => (
  <div className={`${taille} shrink-0 rounded-[10px] overflow-hidden bg-slate-100`}
    style={{ boxShadow: "0 1px 2px rgba(15,27,45,0.12), inset 0 0 0 0.5px rgba(15,27,45,0.08)" }}>
    <img src={s.cover} alt="" loading="lazy" className="w-full h-full object-cover"
      style={{ objectPosition: s.affiche ? "center top" : "center" }} />
  </div>
);

const Fiche = ({ s, a, onFermer, preparer }) => {
  useEffect(() => {
    const echap = (e) => { if (e.key === "Escape") onFermer(); };
    window.addEventListener("keydown", echap);
    return () => window.removeEventListener("keydown", echap);
  }, [onFermer]);
  const texte = s.synopsis || s.description;
  return createPortal(
    <div className="fixed inset-0 z-[95] flex items-end justify-center"
      style={{ background: "rgba(15,27,45,0.45)", paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)" }}
      onClick={onFermer} data-testid="espace-spectacle-fiche">
      <div role="dialog" aria-modal="true" aria-label={s.title} className="w-full max-w-lg px-2.5 max-h-full flex flex-col"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 10px)", color: "var(--text)" }}
        onClick={(e) => e.stopPropagation()}>
        <div className="rounded-[18px] bg-white overflow-hidden min-h-0 flex flex-col">
          <div className="relative shrink-0" style={{ background: s.affiche ? "#0F1B2D" : "#F1F5F9" }}>
            <img src={s.cover} alt="" className={`w-full ${s.affiche ? "max-h-[42vh] object-contain" : "aspect-[16/9] object-cover"}`} />
            <button type="button" onClick={onFermer} aria-label="Fermer" data-testid="espace-spectacle-fiche-fermer"
              className="absolute right-2.5 top-2.5 w-8 h-8 rounded-full grid place-items-center"
              style={{ background: "rgba(15,27,45,0.55)", color: "#fff", backdropFilter: "blur(8px)" }}>
              <X size={16} strokeWidth={2.4} />
            </button>
          </div>
          <div className="px-5 pt-4 pb-5 overflow-y-auto min-h-0 space-y-3">
            <div>
              <div className="text-[12px] uppercase tracking-[0.12em] font-semibold" style={{ color: "#DB2777" }}>{dateCourte(s)}</div>
              <div className="text-[20px] font-semibold leading-tight mt-0.5">{s.title}</div>
              {s.subtitle && <div className="text-[13px] text-[var(--muted)] mt-0.5">{s.subtitle}</div>}
            </div>
            {texte && <p className={`text-[14px] leading-relaxed ${s.synopsis ? "italic" : ""}`}>{texte}</p>}
            {(s.representations || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="espace-spectacle-fiche-dates">
                {s.representations.map((r) => (
                  <span key={`${r.date}-${r.heure}`} className="text-[12.5px] font-medium rounded-full px-2.5 py-1"
                    style={{ background: "var(--primary-tint)", color: "var(--text)" }}>{representation(r)}</span>
                ))}
              </div>
            )}
            {s.location && (
              <Lien href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.location)}`}
                className="flex items-start gap-2 text-[13.5px] leading-snug">
                <MapPin size={16} className="mt-0.5 shrink-0" style={{ color: "var(--primary)" }} />
                <span>{s.location} · <span style={{ color: "var(--primary)" }}>Itinéraire</span></span>
              </Lien>
            )}
            <div className="pt-1">
              {a.lien ? (
                <Lien href={a.lien} onClick={a.billets ? preparer : undefined} className="btn-primary w-full justify-center text-[15px]"
                  data-testid={`espace-spectacle-${s.id}-bouton`}>
                  <Ticket size={17} /> {a.long}
                </Lien>
              ) : a.long ? (
                <div className="text-[13px] text-center text-[var(--muted)] py-2.5 rounded-2xl" style={{ background: "#F4F6F8" }}>{a.long}</div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export const NosSpectacles = ({ moi, billetsOuverts }) => {
  const [ouvert, setOuvert] = useState(null);
  const aujourdhui = new Date().toISOString().slice(0, 10);
  const prochains = SPECTACLES.filter((s) => s.upcoming && (s.date_fin_iso || s.date_iso || "") >= aujourdhui)
    .sort((x, y) => (x.date_iso || "").localeCompare(y.date_iso || ""));
  if (!prochains.length) return null;
  const preparer = () => {
    try {
      sessionStorage.setItem(CLE_CONTACT, JSON.stringify({ prenom: moi.prenom_responsable || "", nom: moi.nom_responsable || "",
        email: moi.email || "", telephone: moi.telephone || "" }));
    } catch { /* navigation privée : la famille remplit elle-même */ }
  };
  const fiche = prochains.find((s) => s.id === ouvert);
  return (
    <section data-testid="espace-nos-spectacles">
      <div className="text-[13px] font-semibold uppercase tracking-[0.12em] text-[var(--muted)] px-1 mb-2">Nos spectacles</div>
      <div className="rounded-3xl bg-white border overflow-hidden" style={{ borderColor: "var(--border)" }}>
        {prochains.map((s, i) => {
          const a = action(s, moi, billetsOuverts);
          return (
            <div key={s.id} className="relative" data-testid={`espace-spectacle-${s.id}`}>
              {i > 0 && <div className="absolute top-0 right-0 left-[78px] h-px" style={{ background: "var(--border)" }} />}
              <div className="flex items-center gap-3 pl-3.5 pr-3 py-3">
                <button type="button" onClick={() => setOuvert(s.id)} className="flex items-center gap-3 min-w-0 flex-1 text-left active:opacity-70"
                  data-testid={`espace-spectacle-${s.id}-ouvrir`} aria-label={`${s.title} : en savoir plus`}>
                  <Vignette s={s} />
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] uppercase tracking-[0.1em] font-semibold" style={{ color: "#DB2777" }}>{dateCourte(s)}</div>
                    {/* La date est juste au-dessus : « Spectacle de Noël », sans « 2026 ». */}
                    <div className="text-[15.5px] font-semibold leading-snug truncate">{String(s.title || "").replace(/\s+\d{4}$/, "")}</div>
                    <div className="text-[12.5px] text-[var(--muted)] truncate">{villeDe(s.location)}</div>
                  </div>
                </button>
                {a.lien ? (
                  <Lien href={a.lien} onClick={a.billets ? preparer : undefined} data-testid={`espace-spectacle-${s.id}-action`}
                    className="shrink-0 rounded-full px-3.5 py-1.5 text-[13px] font-semibold active:scale-95 transition-transform"
                    style={a.reserve ? { background: "#F0FDF4", color: "#15803D" } : { background: "var(--primary-tint)", color: "#008A90" }}>
                    {a.court}
                  </Lien>
                ) : a.court ? (
                  <span className="shrink-0 text-[12.5px] text-[var(--muted)] px-1" data-testid={`espace-spectacle-${s.id}-bientot`}>{a.court}</span>
                ) : <ChevronRight size={18} className="shrink-0 text-slate-300" />}
              </div>
            </div>
          );
        })}
      </div>
      {billetsOuverts && (
        <Lien href="/spectacles/billets#benevoles" onClick={preparer} className="mt-2 flex items-center gap-2 rounded-2xl px-4 py-3 text-sm font-medium"
          style={{ background: "#FDF2F8", color: "#9D174D" }} data-testid="espace-benevole-juin">
          <HeartHandshake size={16} /> Devenir bénévole aux spectacles de juin : un billet offert par créneau
          <ArrowRight size={14} className="ml-auto" />
        </Lien>
      )}
      {fiche && <Fiche s={fiche} a={action(fiche, moi, billetsOuverts)} onFermer={() => setOuvert(null)} preparer={preparer} />}
    </section>
  );
};
